import { useState, useEffect, useRef, useCallback, JSX, MouseEvent } from 'react';
import panzoom, { PanZoom } from 'panzoom';
import { JxtZoomButton, JxtLoader, useIsSmallScreen } from '@jooxter/ui';
import { clsx } from 'clsx';
import { IJxtFloorPlan, IPictoConfig, PictoTypeEnum } from './types';
import { JxtBookingPicto, JxtResourcePicto, JxtUserPicto } from './pictos';
import { computeRatio, getPictoIdFromTarget } from './helper';
import { JxtResourceHeatmapPicto } from './pictos/JxtResourceHeatmapPicto';

const ZOOM_STEP = 0.25;
/** Focusing on a picto: after the plan shows (a modal opening), how close, how long the move. */
const FOCUS_DELAY_MS = 350;
const FOCUS_ZOOM = 2.5;
const FOCUS_MOVE_MS = 700;
/** Locating a space, zoomed in: at most this share of the frame left blank beyond the plan's edges. */
const FOCUS_MAX_MARGIN = 0.5;
const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

/**
 * The blank allowed beyond the plan's edges, locating a space: none at its own size (the plan fills
 * its frame, as always), growing with the zoom up to half the frame at the focus zoom — just enough
 * for a picto at the plan's edge to reach the middle. Zooming out brings the plan back to the edges.
 */
const focusMargin = (size: number, scale: number) =>
  size * FOCUS_MAX_MARGIN * Math.min(1, Math.max(0, (scale - 1) / (FOCUS_ZOOM - 1)));

const JxtFloorPlan = ({
  backgroundImage,
  width,
  height,
  pictos,
  onClick,
  changePictoPriority,
  focusPictoId,
}: IJxtFloorPlan) => {
  const isSmallScreen = useIsSmallScreen();
  const sortedPictos = pictos?.sort((a, b) => b.priority - a.priority) || [];
  const svgRef = useRef<SVGSVGElement>(null);
  const [panzoomInstance, setPanzoomInstance] = useState<PanZoom>();
  const [isReady, setIsReady] = useState(false);
  const [hoveredPictoId, setHoveredPictoId] = useState<number | null>(null);
  const computedRatio = computeRatio(backgroundImage, svgRef.current ?? undefined);
  const isFocusing = focusPictoId !== undefined;

  /**
   * We prevent using the smoothZoom on mobile devices because of performance issues
   */
  const zoomAccordingToScreenSize = useCallback(
    (scaleNumber: number) => {
      if (panzoomInstance && svgRef.current) {
        const clientX = svgRef.current.clientWidth / 2;
        const clientY = svgRef.current.clientHeight / 2;

        if (isSmallScreen) {
          panzoomInstance.zoomTo(clientX, clientY, scaleNumber);
        } else {
          panzoomInstance.smoothZoom(clientX, clientY, scaleNumber);
        }
      }
    },
    [panzoomInstance, isSmallScreen]
  );

  const onPictoClick = (id: number) => {
    onClick && onClick(id);
  };

  /**
   * The hovered picto is resolved from the native target on every move rather than with mouseenter/mouseleave
   * on each picto: bringing the hovered picto to the front moves its node in the DOM, the browser then loses
   * track of it and never fires its mouseleave when the mouse leaves quickly, leaving its tooltip displayed.
   */
  const onSvgMouseMove = (e: MouseEvent<SVGSVGElement>) => {
    const pictoId = getPictoIdFromTarget(e.target);

    if (pictoId === hoveredPictoId) {
      return;
    }

    setHoveredPictoId(pictoId);
    const picto = sortedPictos.find((p) => p.id === pictoId);

    if (picto) {
      changePictoPriority?.(picto);
    }
  };

  useEffect(() => {
    if (!backgroundImage) {
      setIsReady(false);
      return;
    }

    const owner = svgRef.current?.parentElement;

    if (svgRef.current && owner && width && height) {
      try {
        // Its own zoom, once created (1 before: the plan fills its frame).
        const created: { instance?: PanZoom } = {};
        const scale = () => created.instance?.getTransform().scale ?? 1;
        // `true`: the plan always fills its frame. Locating a space, the same at its own size, a blank
        // margin growing with the zoom (`focusMargin`) — read by panzoom each time it keeps the plan
        // in, so it follows the zoom. Same meaning as `true` with a margin of 0.
        const bounds = isFocusing
          ? {
              get left() {
                return owner.clientWidth - focusMargin(owner.clientWidth, scale());
              },
              get right() {
                return focusMargin(owner.clientWidth, scale());
              },
              get top() {
                return owner.clientHeight - focusMargin(owner.clientHeight, scale());
              },
              get bottom() {
                return focusMargin(owner.clientHeight, scale());
              },
            }
          : true;
        const newPanzoomInstance = panzoom(svgRef.current, {
          bounds,
          boundsPadding: 1,
          maxZoom: 5,
          minZoom: 1,
        });

        created.instance = newPanzoomInstance;

        setPanzoomInstance(newPanzoomInstance);
        newPanzoomInstance.setZoomSpeed(0.1);
        setIsReady(true);

        return () => newPanzoomInstance.dispose();
      } catch (error) {
        console.warn(error);
      }
    }
  }, [backgroundImage, width, height, isFocusing]);

  // Locating one space: once the plan is ready, one move — zooming in while its picto glides to the
  // middle; instantly with reduced motion. Once per plan: the user's
  // own zoom and pan are left alone afterwards.
  useEffect(() => {
    const svg = svgRef.current;
    const owner = svg?.parentElement;

    if (!isReady || !panzoomInstance || focusPictoId === undefined || !svg || !owner) {
      return;
    }

    const isReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const marker = () => svg.querySelector<SVGGElement>(`[data-picto-marker="${focusPictoId}"]`);
    let frame: number | undefined;

    const focus = () => {
      const rect = marker()?.getBoundingClientRect();

      if (!rect) {
        return;
      }

      // In the coordinates panzoom moves in (its owner's): where the picto is, where it goes.
      const ownerRect = owner.getBoundingClientRect();
      const from = { x: rect.left + rect.width / 2 - ownerRect.left, y: rect.top + rect.height / 2 - ownerRect.top };
      const to = { x: owner.clientWidth / 2, y: owner.clientHeight / 2 };
      const start = panzoomInstance.getTransform();
      // The picto in the plan's own coordinates: it stays the same point while the plan moves.
      const point = { x: (from.x - start.x) / start.scale, y: (from.y - start.y) / start.scale };
      const place = (progress: number) => {
        const scale = start.scale + (FOCUS_ZOOM - start.scale) * progress;
        const at = { x: from.x + (to.x - from.x) * progress, y: from.y + (to.y - from.y) * progress };

        panzoomInstance.zoomAbs(0, 0, scale);
        panzoomInstance.moveTo(at.x - point.x * scale, at.y - point.y * scale);
      };

      if (isReduced) {
        place(1);
        return;
      }

      const startedAt = performance.now();
      const step = (time: number) => {
        const elapsed = Math.min(1, (time - startedAt) / FOCUS_MOVE_MS);

        place(easeInOutCubic(elapsed));

        if (elapsed < 1) {
          frame = window.requestAnimationFrame(step);
        }
      };

      frame = window.requestAnimationFrame(step);
    };
    const focusTimeout = window.setTimeout(focus, isReduced ? 0 : FOCUS_DELAY_MS);

    return () => {
      window.clearTimeout(focusTimeout);

      if (frame !== undefined) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [isReady, panzoomInstance, focusPictoId]);

  const createPicto = (picto: IPictoConfig, i: number): JSX.Element | null => {
    switch (picto.type) {
      case PictoTypeEnum.Booking:
        return <JxtBookingPicto key={`${picto.x}${picto.y}${i}`} p={picto} onPictoClick={onPictoClick} />;
      case PictoTypeEnum.User:
        return <JxtUserPicto key={`${picto.x}${picto.y}${i}`} p={picto} onPictoClick={onPictoClick} />;
      case PictoTypeEnum.Resource:
        return (
          <JxtResourcePicto
            key={`${picto.id}-${picto.x}${picto.y}`}
            p={picto}
            isHovered={picto.id === hoveredPictoId}
            computedRatio={computedRatio}
            onPictoClick={onPictoClick}
          />
        );
      case PictoTypeEnum.Heatmap:
        return (
          <JxtResourceHeatmapPicto
            key={`${picto.id}-${picto.x}${picto.y}`}
            p={picto}
            isHovered={picto.id === hoveredPictoId}
            computedRatio={computedRatio}
            onPictoClick={onPictoClick}
          />
        );

      default:
        // Invalid picto type, NOOP
        return null;
    }
  };

  return (
    <div className="relative h-full w-full">
      <div className={clsx('h-full w-full', !isReady ? 'opacity-25' : '')}>
        <div id="panzoom" className="h-full w-full overflow-hidden relative flex flex-col justify-center items-center">
          {backgroundImage && (
            <svg
              ref={svgRef}
              xmlns="http://www.w3.org/2000/svg"
              version="1.1"
              preserveAspectRatio="xMidYMid meet"
              className="grow basis-60"
              width="100%"
              height="100%"
              viewBox={`0 0 ${backgroundImage.naturalWidth} ${backgroundImage.naturalHeight}`}
              onMouseMove={onSvgMouseMove}
              onMouseLeave={() => setHoveredPictoId(null)}
            >
              <image
                href={backgroundImage.uri}
                width={backgroundImage.naturalWidth}
                height={backgroundImage.naturalHeight}
              />
              {sortedPictos.map((p, i) => createPicto(p, i))}
            </svg>
          )}
        </div>
        <div className="absolute top-4 right-4">
          <JxtZoomButton
            onZoomIn={() => zoomAccordingToScreenSize(1 + ZOOM_STEP)}
            onZoomOut={() => zoomAccordingToScreenSize(1 - ZOOM_STEP)}
          />
        </div>
      </div>
      {!isReady && (
        <div className="absolute inset-0 flex bg-white z-999 items-center justify-center">
          <JxtLoader />
        </div>
      )}
    </div>
  );
};

export default JxtFloorPlan;
